import { createDiscreteZoomPolicy, THERMAL_ZOOM_LEVELS } from './discreteZoomPolicy';

/**
 * @typedef {{ x: number, y: number, k: number, levelIndex: number }} ZoomTransform
 */

/**
 * 확대된 콘텐츠가 뷰포트를 항상 덮도록 pan 범위를 계산한다.
 * (이미지가 바깥으로 빠져 빈 화면이 보이지 않게)
 *
 * @param {{ x: number, y: number, k: number, contentWidth: number, contentHeight: number, viewportWidth: number, viewportHeight: number }} args
 * @returns {{ x: number, y: number }}
 */
export function clampPanTranslation({
  x,
  y,
  k,
  contentWidth,
  contentHeight,
  viewportWidth,
  viewportHeight
}) {
  const scale = Number(k) > 0 ? Number(k) : 1;
  const cw = Number(contentWidth);
  const ch = Number(contentHeight);
  const vw = Number(viewportWidth);
  const vh = Number(viewportHeight);
  if (![cw, ch, vw, vh].every((n) => Number.isFinite(n) && n > 0)) {
    return { x, y };
  }

  const scaledW = scale * cw;
  const scaledH = scale * ch;
  let nextX = Number(x) || 0;
  let nextY = Number(y) || 0;

  if (scaledW <= vw) {
    // 콘텐츠가 뷰포트보다 작거나 같으면 가운데 고정 (빈 여백이 한쪽으로만 몰리지 않게)
    nextX = (vw - scaledW) / 2;
  } else {
    const minX = vw - scaledW;
    const maxX = 0;
    nextX = Math.min(maxX, Math.max(minX, nextX));
  }

  if (scaledH <= vh) {
    nextY = (vh - scaledH) / 2;
  } else {
    const minY = vh - scaledH;
    const maxY = 0;
    nextY = Math.min(maxY, Math.max(minY, nextY));
  }

  return { x: nextX, y: nextY };
}

/**
 * 이산 줌 엔진 — DOM/프레임워크 비의존.
 * scale `k`는 항상 policy.levels 중 하나만 가진다 (연속 줌 없음).
 *
 * @param {{
 *   levels?: number[],
 *   initialLevelIndex?: number,
 *   viewportWidth?: number,
 *   viewportHeight?: number,
 *   contentWidth?: number,
 *   contentHeight?: number,
 *   clampBounds?: boolean
 * }} [options]
 */
export function createZoomEngine(options = {}) {
  const policy = createDiscreteZoomPolicy(options.levels || THERMAL_ZOOM_LEVELS);
  let x = 0;
  let y = 0;
  let levelIndex = policy.clampIndex(options.initialLevelIndex ?? 0);
  let viewportWidth = options.viewportWidth ?? null;
  let viewportHeight = options.viewportHeight ?? null;
  let contentWidth = options.contentWidth ?? options.viewportWidth ?? null;
  let contentHeight = options.contentHeight ?? options.viewportHeight ?? null;
  let clampBounds = options.clampBounds !== false;
  const listeners = new Set();

  function getTransform() {
    return {
      x,
      y,
      k: policy.levelAt(levelIndex),
      levelIndex
    };
  }

  function applyClamp() {
    if (!clampBounds) {
      return;
    }
    const clamped = clampPanTranslation({
      x,
      y,
      k: policy.levelAt(levelIndex),
      contentWidth,
      contentHeight,
      viewportWidth,
      viewportHeight
    });
    x = clamped.x;
    y = clamped.y;
  }

  function notify() {
    applyClamp();
    const transform = getTransform();
    listeners.forEach((listener) => {
      listener(transform);
    });
    return transform;
  }

  /**
   * 앵커(뷰포트 좌표) 아래 점이 고정되도록 level 변경
   * @param {number} nextIndex
   * @param {number} anchorX
   * @param {number} anchorY
   */
  function setLevelAt(nextIndex, anchorX = 0, anchorY = 0) {
    const clamped = policy.clampIndex(nextIndex);
    if (clamped === levelIndex) {
      return getTransform();
    }
    const oldK = policy.levelAt(levelIndex);
    const newK = policy.levelAt(clamped);
    const ax = Number(anchorX) || 0;
    const ay = Number(anchorY) || 0;
    x = ax - ((ax - x) * newK) / oldK;
    y = ay - ((ay - y) * newK) / oldK;
    levelIndex = clamped;
    return notify();
  }

  return {
    getPolicy() {
      return policy;
    },
    getTransform,
    /**
     * @param {(t: ZoomTransform) => void} listener
     * @returns {() => void} unsubscribe
     */
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setBounds({
      viewportWidth: vw,
      viewportHeight: vh,
      contentWidth: cw,
      contentHeight: ch,
      clampBounds: clamp
    } = {}) {
      if (vw != null) viewportWidth = vw;
      if (vh != null) viewportHeight = vh;
      if (cw != null) contentWidth = cw;
      if (ch != null) contentHeight = ch;
      if (clamp != null) clampBounds = !!clamp;
      return notify();
    },
    zoomInAt(anchorX, anchorY) {
      if (!policy.canZoomIn(levelIndex)) {
        return getTransform();
      }
      return setLevelAt(policy.nextIndex(levelIndex), anchorX, anchorY);
    },
    zoomOutAt(anchorX, anchorY) {
      if (!policy.canZoomOut(levelIndex)) {
        return getTransform();
      }
      return setLevelAt(policy.prevIndex(levelIndex), anchorX, anchorY);
    },
    setLevelIndex(index, anchorX, anchorY) {
      return setLevelAt(index, anchorX, anchorY);
    },
    panBy(dx, dy) {
      x += Number(dx) || 0;
      y += Number(dy) || 0;
      return notify();
    },
    setPan(nextX, nextY) {
      x = Number(nextX) || 0;
      y = Number(nextY) || 0;
      return notify();
    },
    reset() {
      x = 0;
      y = 0;
      levelIndex = 0;
      return notify();
    },
    /**
     * @param {number} deltaY wheel deltaY (음수=확대)
     * @param {number} anchorX
     * @param {number} anchorY
     */
    handleWheel(deltaY, anchorX, anchorY) {
      if (deltaY < 0) {
        return this.zoomInAt(anchorX, anchorY);
      }
      if (deltaY > 0) {
        return this.zoomOutAt(anchorX, anchorY);
      }
      return getTransform();
    },
    /**
     * CSS / SVG 에 바로 넣을 문자열
     * @param {ZoomTransform} [transform]
     */
    toCssTransform(transform) {
      const t = transform || getTransform();
      return `translate(${t.x}px, ${t.y}px) scale(${t.k})`;
    }
  };
}
